import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { 
  Shield, ShieldAlert, ShieldCheck, ShieldX,
  TrendingUp, TrendingDown, AlertTriangle,
  ChevronRight, Sparkles, Loader2, Users
} from 'lucide-react';
import { useLanguage } from '../i18n';

const getRiskLevelConfig = (level, t) => {
  const configs = {
    excellent: {
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
      textColor: 'text-emerald-700 dark:text-emerald-400',
      icon: ShieldCheck,
      label: t('risk.levels.excellent')
    },
    good: {
      color: 'bg-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-700 dark:text-green-400',
      icon: Shield,
      label: t('risk.levels.good')
    },
    moderate: {
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-700 dark:text-yellow-400',
      icon: Shield,
      label: t('risk.levels.moderate')
    },
    high: {
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      textColor: 'text-orange-700 dark:text-orange-400',
      icon: ShieldAlert,
      label: t('risk.levels.high')
    },
    critical: {
      color: 'bg-red-500',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      textColor: 'text-red-700 dark:text-red-400',
      icon: ShieldX,
      label: t('risk.levels.critical')
    }
  };
  return configs[level] || configs.moderate;
};

// Member Risk Score Badge (small)
export const MemberRiskBadge = ({ score, level, onClick }) => {
  const { t } = useLanguage();
  const config = getRiskLevelConfig(level, t);
  const Icon = config.icon;
  
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all hover:scale-105 ${config.bgColor} ${config.textColor}`}
      title={t('risk.badgeTitle', { score })}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{score}</span>
    </button>
  );
};

// Member Risk Detail Dialog
export const MemberRiskDialog = ({ 
  tontineId, 
  memberId, 
  memberName,
  isOpen,
  onClose 
}) => {
  const [loading, setLoading] = useState(true);
  const [riskData, setRiskData] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen && memberId) {
      loadRiskScore();
    }
  }, [isOpen, memberId]);

  const loadRiskScore = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getMemberRiskScore(tontineId, memberId, true);
      setRiskData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('risk.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const config = riskData ? getRiskLevelConfig(riskData.level, t) : null;
  const Icon = config?.icon || Shield;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-600" />
            {t('risk.memberDialogTitle', { memberName })}
          </DialogTitle>
          <DialogDescription>
            {t('risk.memberDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        ) : riskData && (
          <div className="space-y-4">
            {/* Score Overview */}
            <div className={`p-4 rounded-xl ${config.bgColor}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-6 h-6 ${config.textColor}`} />
                  <span className={`font-semibold ${config.textColor}`}>
                    {config.label}
                  </span>
                </div>
                <span className={`text-3xl font-bold ${config.textColor}`}>
                  {riskData.score}/100
                </span>
              </div>
              <Progress 
                value={riskData.score} 
                className={`h-2 ${config.bgColor}`}
              />
            </div>

            {/* Factors Breakdown */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-stone-700 dark:text-stone-300">
                {t('risk.factorsTitle')}
              </h4>
              {Object.entries(riskData.factors).map(([key, factor]) => {
                const label = t(`risk.factors.${key}`);
                const fallback = key.replace(/_/g, ' ');
                return (
                <div key={key} className="flex items-center justify-between py-2 border-b border-stone-100 dark:border-stone-800">
                  <div className="flex items-center gap-2">
                    <span className="text-sm capitalize">
                      {label === `risk.factors.${key}` ? fallback : label}
                    </span>
                    <span className="text-xs text-stone-400">
                      ({Math.round(factor.weight * 100)}%)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-stone-100 dark:bg-stone-800 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full ${factor.score >= 70 ? 'bg-green-500' : factor.score >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${factor.score}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-8 text-right">
                      {factor.score}
                    </span>
                  </div>
                </div>
                );
              })}
            </div>

            {/* AI Analysis */}
            {riskData.ai_analysis && (
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">
                    {t('risk.aiAnalysisTitle')}
                  </span>
                </div>
                <p className="text-sm text-purple-800 dark:text-purple-300">
                  {riskData.ai_analysis}
                </p>
              </div>
            )}

            {/* Recommendations */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-stone-700 dark:text-stone-300">
                {t('risk.recommendationsTitle')}
              </h4>
              <ScrollArea className="max-h-32">
                {riskData.recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-2 py-1.5">
                    <ChevronRight className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-stone-600 dark:text-stone-400">{rec}</p>
                  </div>
                ))}
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Tontine Risk Summary Card
export const TontineRiskSummary = ({ tontineId }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSummary();
  }, [tontineId]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const response = await api.getTontineRiskSummary(tontineId);
      setSummary(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null; // Silently fail for non-admins
  }

  if (!summary) return null;

  const avgConfig = getRiskLevelConfig(
    summary.average_score >= 90 ? 'excellent' :
    summary.average_score >= 70 ? 'good' :
    summary.average_score >= 50 ? 'moderate' :
    summary.average_score >= 30 ? 'high' : 'critical',
    t
  );

  return (
    <Card data-testid="risk-summary-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="w-5 h-5 text-orange-600" />
          {t('risk.summaryTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Average Score */}
        <div className={`p-3 rounded-lg ${avgConfig.bgColor} flex items-center justify-between`}>
          <div>
            <p className="text-xs text-stone-500">{t('risk.averageScore')}</p>
            <p className={`text-2xl font-bold ${avgConfig.textColor}`}>
              {summary.average_score.toFixed(0)}/100
            </p>
          </div>
          <Users className={`w-8 h-8 ${avgConfig.textColor} opacity-50`} />
        </div>

        {/* Risk Distribution */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-stone-500">{t('risk.distribution')}</p>
          <div className="flex gap-1">
            {['excellent', 'good', 'moderate', 'high', 'critical'].map(level => {
              const count = summary.risk_distribution[level] || 0;
              const config = getRiskLevelConfig(level, t);
              if (count === 0) return null;
              return (
                <div
                  key={level}
                  className={`flex-1 h-8 ${config.color} rounded flex items-center justify-center`}
                  style={{ flex: count }}
                  title={t('risk.distributionItemTitle', { level: config.label, count })}
                >
                  <span className="text-white text-xs font-bold">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* High Risk Alert */}
        {summary.high_risk_members.length > 0 && (
          <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-xs font-semibold text-red-700 dark:text-red-400">
                {t('risk.highRiskMembers', { count: summary.high_risk_members.length })}
              </span>
            </div>
            <div className="space-y-1">
              {summary.high_risk_members.slice(0, 3).map(m => (
                <div key={m.member_id} className="flex items-center justify-between text-xs">
                  <span className="text-stone-600 dark:text-stone-400">{m.user_name}</span>
                  <Badge variant="destructive" className="text-[10px]">{m.score}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Members */}
        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
              {t('risk.topMembers')}
            </span>
          </div>
          <p data-testid="top-members-threshold-hint" className="text-[11px] text-stone-500 mb-1">
            {t('risk.topMembersThresholdHint', { minScore: summary.top_members_min_score ?? 50 })}
          </p>
          {summary.top_members.length > 0 ? (
            <div className="space-y-1">
              {summary.top_members.map(m => (
                <div key={m.member_id} className="flex items-center justify-between text-xs">
                  <span className="text-stone-600 dark:text-stone-400">{m.user_name}</span>
                  <Badge className="bg-emerald-500 text-[10px]">{m.score}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-stone-600 dark:text-stone-400">
              {t('risk.topMembersEmpty', { minScore: 50 })}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TontineRiskSummary;
